import { useState } from 'react';
import { useOpsEvents, useOpsHealth } from '@/hooks/useConsoleData';
import { Section } from '@/components/console/Section';
import { checkLabel, groupChecks, headline, type OpsCheck, type OpsEventSource } from '@/lib/ops-data';
import { NOT_MEASURED, ago, num, when } from '@/lib/format';
import s from './console.module.css';

/**
 * System: the state of everything, as the watchtower saw it on its last
 * 5-minute run. One tile per check (green, amber, red, grey for "not
 * configured"), the fix line on anything that is not green, "fixed
 * automatically at HH:MM" when the watchtower did it, the dead-man's
 * status, then one event stream merging Sentry, Stripe, RevenueCat,
 * PostHog and the watchtower's own findings. Nothing here is estimated: a
 * source without a key says so.
 */

const SOURCES: { key: OpsEventSource | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'watchtower', label: 'Watchtower' },
  { key: 'sentry', label: 'Sentry' },
  { key: 'stripe', label: 'Stripe' },
  { key: 'revenuecat', label: 'RevenueCat' },
  { key: 'posthog', label: 'PostHog' },
];

function Tile({ c }: { c: OpsCheck }) {
  const fixedAt = c.remediated ? when(c.lastNotifiedAt ?? c.firstSeenAt ?? null, '') : '';
  return (
    <div className={`${s.tile} ${s[`tile_${c.status}`]}`} data-check={c.name}>
      <div className={s.tileHead}>
        <span className={`${s.dot} ${s[`dot_${c.status}`]}`} aria-hidden />
        <span className={s.tileName}>{checkLabel(c.name)}</span>
        <span className={s.pill}>{c.status === 'skipped' ? 'not configured' : c.status}</span>
      </div>
      <div className={s.tileDetail}>{c.detail || (c.status === 'ok' ? 'ok' : NOT_MEASURED)}</div>
      {c.remediated && (
        <div className={`${s.tileRem} ${c.remediationFailed ? s.tileRem_failed : ''}`}>
          {c.remediationFailed ? 'Automatic fix failed' : `Fixed automatically${fixedAt ? ` at ${fixedAt}` : ''}`}{c.remediation ? `: ${c.remediation}` : ''}
        </div>
      )}
      {c.remediationFailed && !c.remediated && c.remediation && <div className={`${s.tileRem} ${s.tileRem_failed}`}>Automatic fix failed: {c.remediation}</div>}
      {c.fix && c.status !== 'ok' && <div className={s.tileFix}>{c.fix}</div>}
      {c.firstSeenAt && c.status !== 'ok' && c.status !== 'skipped' && <div className={s.muted} style={{ fontSize: '0.74rem' }}>since {when(c.firstSeenAt)}</div>}
    </div>
  );
}

export default function SystemPage() {
  const health = useOpsHealth();
  const events = useOpsEvents();
  const [source, setSource] = useState<OpsEventSource | 'all'>('all');
  const h = health.data;
  const r = h?.latest ?? null;
  const head = headline(h);
  const groups = r ? groupChecks(r.checks) : [];
  const ev = events.data;
  const shown = ev ? ev.events.filter((e) => source === 'all' || e.source === source) : [];
  const maxHour = ev?.posthogHourly ? Math.max(1, ...ev.posthogHourly.map((x) => x.count)) : 1;

  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <h1 className={s.pageTitle}>System</h1>
        <span className={s.pageSub}>
          {r ? <>last run {ago(r.finishedAt)} in {Math.round(r.durationMs / 1000)} s{r.runUrl ? <> · <a className={s.linkA} href={r.runUrl} target="_blank" rel="noreferrer">open the run</a></> : null}</> : 'no run yet'}
          {' · '}
          <a className={s.linkA} href="https://github.com/Posture-Vision/pv-auth-service/blob/main/docs/ops/WATCHTOWER.md" target="_blank" rel="noreferrer">runbook</a>
        </span>
      </div>

      {health.isError && <div className={s.error}>GET /provider/admin/ops/health did not load. The board is {NOT_MEASURED} right now; the watchtower still pages on its own.</div>}

      <div className={`${s.headline} ${s[`headline_${head.tone}`]}`} role="status">
        <span className={`${s.dot} ${s[`dot_${head.tone}`]}`} aria-hidden />
        <span>{head.text}</span>
      </div>

      {h && (
        <div className={s.strip}>
          <div className={s.stat}>
            <span className={s.statLabel}>Dead-man's switch</span>
            <span className={s.statVal} style={{ fontSize: '1.2rem' }}>{h.deadMan.silent ? 'SILENT' : 'alive'}</span>
            <span className={s.statSub}>{h.deadMan.lastRunAt ? `last heartbeat ${ago(h.deadMan.lastRunAt)}` : 'never ran'} · pages after {h.deadMan.silentAfterMinutes} min via {h.deadMan.alarm}</span>
          </div>
          <div className={s.stat}><span className={s.statLabel}>Runs, {h.window.hours} h</span><span className={s.statVal}>{num(h.window.runs)}</span><span className={s.statSub}>every {h.deadMan.intervalMinutes} min from GitHub, no laptop involved</span></div>
          <div className={s.stat}><span className={s.statLabel}>Runs with a red check</span><span className={s.statVal}>{num(h.window.critical)}</span><span className={s.statSub}>{num(h.window.degraded)} degraded only</span></div>
          <div className={s.stat}><span className={s.statLabel}>Fixed automatically</span><span className={s.statVal}>{num(h.window.remediations)}</span><span className={s.statSub}>restarts, prunes, redeploys</span></div>
          <div className={s.stat}>
            <span className={s.statLabel}>Not configured</span>
            <span className={s.statVal}>{r ? num(r.checks.filter((c) => c.status === 'skipped').length) : NOT_MEASURED}</span>
            <span className={s.statSub}>{r && r.secretsMissing?.length ? r.secretsMissing.map((p) => p.split('/').pop()).join(', ') : 'every key present'}</span>
          </div>
        </div>
      )}

      {h && h.history.length > 0 && (
        <div>
          <div className={s.groupTitle}>Last {h.history.length} runs, oldest first</div>
          <div className={s.runs}>
            {[...h.history].reverse().map((row) => (
              <a key={row.id} className={`${s.run} ${s[`run_${row.overall}`]}`} href={row.runUrl ?? undefined} target="_blank" rel="noreferrer"
                title={`${when(row.finishedAt)}: ${row.overall}${row.critical.length ? `, critical: ${row.critical.join(', ')}` : ''}${row.remediated.length ? `, fixed: ${row.remediated.join(', ')}` : ''}`} />
            ))}
          </div>
        </div>
      )}

      {groups.map((g) => (
        <div key={g.key}>
          <div className={s.groupTitle}>{g.label}</div>
          <div className={s.tiles}>{g.checks.map((c) => <Tile key={c.name} c={c} />)}</div>
        </div>
      ))}

      {r?.alerts && (
        <Section title="Who was told, last run" summary={`Pushover ${r.alerts.pushover.length}, email ${r.alerts.email.length}, Slack ${r.alerts.slack.length}${r.redeployDispatched ? ' · Deploy to Production re-dispatched' : ''}`}>
          <dl className={s.facts}>
            <dt>Pushover</dt><dd>{r.alerts.pushover.length ? r.alerts.pushover.map((a) => `${a.kind} ${a.ok ? 'delivered' : 'FAILED'}`).join(', ') : 'nothing to page'}</dd>
            <dt>Email (An + Brian)</dt><dd>{r.alerts.email.length ? r.alerts.email.map((a) => `${a.kind} ${a.ok ? 'accepted by SendGrid' : 'FAILED'}`).join(', ') : 'nothing critical'}</dd>
            <dt>Slack</dt><dd>{r.alerts.slack.length ? r.alerts.slack.map((a) => `${a.kind} ${a.ok ? 'posted' : 'FAILED'}`).join(', ') : 'nothing to note'}</dd>
          </dl>
        </Section>
      )}

      <Section title="Events" summary={ev ? `Sentry, Stripe, RevenueCat, PostHog and the watchtower, last 24 h · measured ${when(ev.generatedAt)}` : events.isError ? 'did not load (GET /provider/admin/ops/events)' : 'reading'} count={ev ? shown.length : null} defaultOpen>
        {ev && (
          <div className={s.stack}>
            <div className={s.filters}>
              {SOURCES.map((f) => {
                const st = f.key === 'all' ? null : ev.sources[f.key];
                const n = f.key === 'all' ? ev.events.length : ev.events.filter((e) => e.source === f.key).length;
                return (
                  <button key={f.key} type="button" className={`${s.filter} ${source === f.key ? s.filterOn : ''}`} onClick={() => setSource(f.key)} title={st ? st.note : ''}>
                    {f.label}{st && !st.configured ? ' (not configured)' : st && !st.ok ? ' (error)' : ''}<span className={s.filterCount}>{n}</span>
                  </button>
                );
              })}
            </div>
            {(Object.keys(ev.sources) as OpsEventSource[]).filter((k) => !ev.sources[k].configured || !ev.sources[k].ok).length > 0 && (
              <div className={s.notice}>
                {(Object.keys(ev.sources) as OpsEventSource[]).filter((k) => !ev.sources[k].configured || !ev.sources[k].ok).map((k) => `${k}: ${ev.sources[k].note}`).join(' · ')}
              </div>
            )}
            {ev.posthogHourly && (source === 'all' || source === 'posthog') && (
              <div>
                <div className={s.groupTitle}>PostHog events per hour, last 24 h</div>
                <div className={s.bars}>
                  {ev.posthogHourly.map((x) => <div key={x.hour} className={s.bar} style={{ height: `${Math.max(2, (x.count / maxHour) * 100)}%` }} title={`${when(x.hour)}: ${num(x.count)} events`} />)}
                </div>
              </div>
            )}
            <div className={s.events}>
              {shown.length === 0 && <div className={s.empty}>Nothing in the window from {source === 'all' ? 'any source' : source}.</div>}
              {shown.map((e, i) => (
                <div key={`${e.source}-${e.at}-${i}`} className={`${s.event} ${e.level === 'critical' ? s.event_critical : e.level === 'warn' ? s.event_warn : ''}`}>
                  <span className={s.eventAt}>{when(e.at)}</span>
                  <span className={s.pill}>{e.source}</span>
                  <span>
                    <span className={s.eventTitle}>{e.url ? <a className={s.linkA} href={e.url} target="_blank" rel="noreferrer">{e.title}</a> : e.title}</span>
                    {e.detail && <span className={s.eventDetail}> · {e.detail}</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}
